import Http from './Http';

/**
 * Interface for a tenant option
 */
export interface TenantOption {
    OptionId?: number;
    OptionKey: string;
    OptionValue?: string | null;
    RefKey?: string | null;
    RefVal?: string | null;
    CreatedDate?: string;
    ModifiedDate?: string | null;
}

/**
 * Interface for saving an option
 */
export interface SaveOptionRequest {
    OptionKey: string;
    OptionValue?: string | null;
    RefKey?: string | null;
    RefVal?: string | null;
}

/**
 * Service for managing tenant-specific options (key-value settings).
 * Options are stored per-tenant and can optionally reference specific records.
 * 
 * Usage Examples:
 * 
 * // Save a simple option
 * await OptionsService.set('theme', 'dark');
 * 
 * // Save an option for a specific user
 * await OptionsService.set('color', '#FF5733', 'StaffId', '123');
 * 
 * // Get an option
 * const theme = await OptionsService.get('theme');
 * 
 * // Get all SMTP options
 * const smtpOptions = await OptionsService.getByPrefix('smtp_');
 * 
 * // Save multiple options at once
 * await OptionsService.setBulk([
 *     { OptionKey: 'smtp_host', OptionValue: 'smtp.gmail.com' },
 *     { OptionKey: 'smtp_port', OptionValue: '587' }
 * ]);
 * 
 * // Delete an option
 * await OptionsService.delete('old_setting');
 */
class OptionsService {
    
    /**
     * Get a single option by key
     * @param key The option key
     * @param refKey Optional reference key (e.g., 'StaffId')
     * @param refVal Optional reference value (e.g., '123')
     * @returns The option value or null if not found
     */
    async get(key: string, refKey?: string, refVal?: string): Promise<string | null> {
        const params = new URLSearchParams();
        if (refKey) params.append('refKey', refKey);
        if (refVal) params.append('refVal', refVal);
        
        const queryString = params.toString();
        const url = `/options/${encodeURIComponent(key)}${queryString ? '?' + queryString : ''}`;
        
        // Http.get returns data.Data directly
        const data = await Http.get(url);
        return data?.OptionValue ?? null;
    }

    /**
     * Get the full option object by key
     * @param key The option key
     * @param refKey Optional reference key
     * @param refVal Optional reference value
     * @returns The full option object or null if not found
     */
    async getOption(key: string, refKey?: string, refVal?: string): Promise<TenantOption | null> {
        const params = new URLSearchParams();
        if (refKey) params.append('refKey', refKey);
        if (refVal) params.append('refVal', refVal);
        
        const queryString = params.toString();
        const url = `/options/${encodeURIComponent(key)}${queryString ? '?' + queryString : ''}`;
        
        // Http.get returns data.Data directly
        const data = await Http.get(url);
        return data ?? null;
    }

    /**
     * Get all options (optionally filtered)
     * @param prefix Optional prefix to filter by (e.g., 'smtp_')
     * @param refKey Optional reference key to filter by
     * @param refVal Optional reference value to filter by
     * @returns Array of options
     */
    async getAll(prefix?: string, refKey?: string, refVal?: string): Promise<TenantOption[]> {
        const params = new URLSearchParams();
        if (prefix) params.append('prefix', prefix);
        if (refKey) params.append('refKey', refKey);
        if (refVal) params.append('refVal', refVal);
        
        const queryString = params.toString();
        const url = `/options${queryString ? '?' + queryString : ''}`;
        
        // Http.get returns data.Data directly, not the full response
        const data = await Http.get(url);
        return data ?? [];
    }

    /**
     * Get options by prefix and convert to a key-value object
     * @param prefix The prefix to filter by (e.g., 'smtp_')
     * @param stripPrefix Whether to strip the prefix from keys in the result
     * @returns Object with option keys as properties
     */
    async getByPrefix(prefix: string, stripPrefix: boolean = false): Promise<Record<string, string | null>> {
        const options = await this.getAll(prefix);
        const result: Record<string, string | null> = {};
        
        for (const opt of options) {
            const key = stripPrefix ? opt.OptionKey.replace(prefix, '') : opt.OptionKey;
            result[key] = opt.OptionValue ?? null;
        }
        
        return result;
    }

    /**
     * Get options for a specific reference (e.g., all options for a specific user)
     * @param refKey The reference key (e.g., 'StaffId')
     * @param refVal The reference value (e.g., '123')
     * @returns Object with option keys as properties
     */
    async getByRef(refKey: string, refVal: string): Promise<Record<string, string | null>> {
        const options = await this.getAll(undefined, refKey, refVal);
        const result: Record<string, string | null> = {};
        
        for (const opt of options) {
            result[opt.OptionKey] = opt.OptionValue ?? null;
        }
        
        return result;
    }

    /**
     * Set a single option (creates or updates)
     * @param key The option key
     * @param value The option value
     * @param refKey Optional reference key
     * @param refVal Optional reference value
     */
    async set(key: string, value: string | null, refKey?: string, refVal?: string): Promise<void> {
        await Http.post('/options', {
            OptionKey: key,
            OptionValue: value,
            RefKey: refKey,
            RefVal: refVal
        });
    }

    /**
     * Set multiple options at once
     * @param options Array of options to save
     */
    async setBulk(options: SaveOptionRequest[]): Promise<void> {
        await Http.post('/options/bulk', { Options: options });
    }

    /**
     * Set multiple options with a common prefix
     * @param prefix The prefix to add to all keys
     * @param values Object with keys and values
     * @param refKey Optional reference key for all options
     * @param refVal Optional reference value for all options
     */
    async setWithPrefix(prefix: string, values: Record<string, string | null>, refKey?: string, refVal?: string): Promise<void> {
        const options: SaveOptionRequest[] = Object.entries(values).map(([key, value]) => ({
            OptionKey: prefix + key,
            OptionValue: value,
            RefKey: refKey,
            RefVal: refVal
        }));
        
        await this.setBulk(options);
    }

    /**
     * Delete a single option
     * @param key The option key
     * @param refKey Optional reference key
     * @param refVal Optional reference value
     */
    async delete(key: string, refKey?: string, refVal?: string): Promise<void> {
        await Http.post('/options/delete', {
            OptionKey: key,
            RefKey: refKey,
            RefVal: refVal
        });
    }

    /**
     * Delete all options with a given prefix
     * @param prefix The prefix to match
     */
    async deleteByPrefix(prefix: string): Promise<void> {
        await Http.post('/options/delete-by-prefix', { Prefix: prefix });
    }

    // ============================================
    // Convenience methods for common use cases
    // ============================================

    /**
     * Get SMTP settings as an object
     * @returns SMTP configuration object
     */
    async getSmtpSettings(): Promise<SmtpSettings> {
        const options = await this.getByPrefix('smtp_', true);
        const port = parseInt(options['port'] || '587', 10);
        return {
            host: options['host'] || '',
            port: isNaN(port) ? 587 : port,
            username: options['username'] || '',
            password: options['password'] || '',
            fromEmail: options['from_email'] || '',
            fromName: options['from_name'] || '',
            enableSsl: options['enable_ssl'] === 'true'
        };
    }

    /**
     * Save SMTP settings
     * @param settings SMTP configuration object
     */
    async saveSmtpSettings(settings: SmtpSettings): Promise<void> {
        await this.setWithPrefix('smtp_', {
            'host': settings.host,
            'port': settings.port.toString(),
            'username': settings.username,
            'password': settings.password,
            'from_email': settings.fromEmail,
            'from_name': settings.fromName,
            'enable_ssl': settings.enableSsl ? 'true' : 'false'
        });
    }

    /**
     * Get Microsoft Graph settings as an object
     * @returns Graph configuration object
     */
    async getGraphSettings(): Promise<GraphSettings> {
        const options = await this.getByPrefix('graph_', true);
        return {
            tenantId: options['tenant_id'] || '',
            clientId: options['client_id'] || '',
            clientSecret: options['client_secret'] || '',
            fromEmail: options['from_email'] || '',
            fromName: options['from_name'] || ''
        };
    }

    /**
     * Save Microsoft Graph settings
     * @param settings Graph configuration object
     */
    async saveGraphSettings(settings: GraphSettings): Promise<void> {
        await this.setWithPrefix('graph_', {
            'tenant_id': settings.tenantId,
            'client_id': settings.clientId,
            'client_secret': settings.clientSecret,
            'from_email': settings.fromEmail,
            'from_name': settings.fromName
        });
    }

    /**
     * Get the current email provider setting
     * @returns 'smtp' or 'graph'
     */
    async getEmailProvider(): Promise<'smtp' | 'graph'> {
        const provider = await this.get('email_provider');
        return (provider === 'graph') ? 'graph' : 'smtp';
    }

    /**
     * Save the email provider setting
     * @param provider 'smtp' or 'graph'
     */
    async saveEmailProvider(provider: 'smtp' | 'graph'): Promise<void> {
        await this.set('email_provider', provider);
    }

    /**
     * Get user preferences for a specific user
     * @param userId The user ID (StaffId or TechnicianId)
     * @param userType The type of user ('Staff' or 'Technician')
     * @returns User preferences object
     */
    async getUserPreferences(userId: string, userType: 'Staff' | 'Technician' = 'Staff'): Promise<Record<string, string | null>> {
        return await this.getByRef(userType + 'Id', userId);
    }

    /**
     * Save a user preference
     * @param userId The user ID
     * @param userType The type of user
     * @param key The preference key
     * @param value The preference value
     */
    async setUserPreference(userId: string, userType: 'Staff' | 'Technician', key: string, value: string | null): Promise<void> {
        await this.set(key, value, userType + 'Id', userId);
    }

    /**
     * Save multiple user preferences
     * @param userId The user ID
     * @param userType The type of user
     * @param preferences Object with preference keys and values
     */
    async setUserPreferences(userId: string, userType: 'Staff' | 'Technician', preferences: Record<string, string | null>): Promise<void> {
        const options: SaveOptionRequest[] = Object.entries(preferences).map(([key, value]) => ({
            OptionKey: key,
            OptionValue: value,
            RefKey: userType + 'Id',
            RefVal: userId
        }));
        
        await this.setBulk(options);
    }

    /**
     * Get Elevate SMS settings as an object
     * @returns Elevate configuration object
     */
    async getElevateSettings(): Promise<ElevateSettings> {
        const options = await this.getByPrefix('elevate_', true);
        return {
            clientId: options['client_id'] || '',
            clientSecret: options['client_secret'] || '',
            authBaseUrl: options['auth_base_url'] || 'https://auth.intermedia.com',
            apiBaseUrl: options['api_base_url'] || 'https://api.intermedia.com',
            fromNumber: options['from_number'] || '',
            enabled: options['enabled'] === 'true'
        };
    }

    /**
     * Save Elevate SMS settings
     * @param settings Elevate configuration object
     */
    async saveElevateSettings(settings: ElevateSettings): Promise<void> {
        await this.setWithPrefix('elevate_', {
            'client_id': settings.clientId,
            'client_secret': settings.clientSecret,
            'auth_base_url': settings.authBaseUrl,
            'api_base_url': settings.apiBaseUrl,
            'from_number': settings.fromNumber,
            'enabled': settings.enabled ? 'true' : 'false'
        });
    }
}

/**
 * SMTP Settings interface
 */
export interface SmtpSettings {
    host: string;
    port: number;
    username: string;
    password: string;
    fromEmail: string;
    fromName: string;
    enableSsl: boolean;
}

/**
 * Microsoft Graph Settings interface
 */
export interface GraphSettings {
    tenantId: string;
    clientId: string;
    clientSecret: string;
    fromEmail: string;
    fromName: string;
}

/**
 * Elevate SMS Settings interface
 */
export interface ElevateSettings {
    clientId: string;
    clientSecret: string;
    authBaseUrl: string;
    apiBaseUrl: string;
    fromNumber: string;
    enabled: boolean;
}

// Export singleton instance
const optionsService = new OptionsService();
export default optionsService;
